import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Clock, Instagram, Facebook, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';

const Contacto = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    asunto: '',
    mensaje: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('¡Gracias por contactarnos! Te responderemos pronto.');
    setFormData({ nombre: '', email: '', asunto: '', mensaje: '' });
  };

  const infoContacto = [
    {
      icono: Mail,
      titulo: 'Email',
      valor: 'hola@luminaastrologia.com',
      descripcion: 'Respondemos en 24-48 horas',
    },
    {
      icono: Phone,
      titulo: 'Teléfono',
      valor: '+1 (555) 123-4567',
      descripcion: 'Lunes a Viernes, 9am - 6pm',
    },
    {
      icono: MapPin,
      titulo: 'Ubicación',
      valor: 'Ciudad de México, México',
      descripcion: 'Consultas presenciales con cita',
    },
    {
      icono: Clock,
      titulo: 'Horario',
      valor: 'Lun - Vie: 9:00 - 18:00',
      descripcion: 'Sábados: 10:00 - 14:00',
    },
  ];

  const redesSociales = [
    { icono: Instagram, nombre: 'Instagram', url: '#' },
    { icono: Facebook, nombre: 'Facebook', url: '#' },
    { icono: Youtube, nombre: 'YouTube', url: '#' },
  ];

  return (
    <div className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Badge className="mb-4 bg-purple-500/20 text-purple-200 border-purple-400/30">
            <Send className="w-4 h-4 mr-2" />
            Contacto
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gradient mb-4">
            Conecta con Nosotros
          </h2>
          <p className="text-lg text-purple-100/60 max-w-2xl mx-auto">
            ¿Tienes preguntas sobre nuestros cursos, servicios o simplemente quieres saludar? 
            Estamos aquí para ayudarte.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <motion.div
            className="lg:col-span-1 space-y-4"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {infoContacto.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-purple-950/40 border-purple-500/20 hover:border-purple-400/40 transition-all">
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-purple-500/20">
                      <info.icono className="w-5 h-5 text-purple-300" />
                    </div>
                    <div>
                      <h4 className="text-sm text-purple-200/50 mb-1">{info.titulo}</h4>
                      <p className="text-purple-100 font-medium">{info.valor}</p>
                      <p className="text-sm text-purple-200/50">{info.descripcion}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}

            {/* Social Media */}
            <Card className="bg-purple-950/40 border-purple-500/20">
              <CardContent className="p-5">
                <h4 className="text-purple-100 font-medium mb-4">Síguenos</h4>
                <div className="flex gap-3">
                  {redesSociales.map((red, index) => (
                    <motion.a
                      key={index}
                      href={red.url}
                      className="p-3 rounded-xl bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 hover:text-purple-200 transition-colors"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title={red.nombre}
                    >
                      <red.icono className="w-5 h-5" />
                    </motion.a>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Card className="bg-purple-950/60 border-purple-500/30">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-purple-100 mb-6">
                  Envíanos un Mensaje
                </h3>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-purple-200/70 mb-2">
                        Nombre Completo
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-purple-950/50 border border-purple-500/30 text-purple-100 placeholder:text-purple-400/50 focus:outline-none focus:border-purple-400"
                        placeholder="Tu nombre"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-purple-200/70 mb-2">
                        Correo Electrónico
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-purple-950/50 border border-purple-500/30 text-purple-100 placeholder:text-purple-400/50 focus:outline-none focus:border-purple-400"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-purple-200/70 mb-2">
                      Asunto
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.asunto}
                      onChange={(e) => setFormData({ ...formData, asunto: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-purple-950/50 border border-purple-500/30 text-purple-100 placeholder:text-purple-400/50 focus:outline-none focus:border-purple-400"
                      placeholder="¿Sobre qué nos quieres contactar?"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-purple-200/70 mb-2">
                      Mensaje
                    </label>
                    <textarea
                      required
                      rows={5}
                      value={formData.mensaje}
                      onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-purple-950/50 border border-purple-500/30 text-purple-100 placeholder:text-purple-400/50 focus:outline-none focus:border-purple-400 resize-none"
                      placeholder="Escribe tu mensaje aquí..."
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white py-6"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Enviar Mensaje
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* FAQ Section */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h3 className="text-2xl font-bold text-purple-100 text-center mb-8">
            Preguntas Frecuentes
          </h3>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                pregunta: '¿Cuánto tiempo tarda una lectura de carta natal?',
                respuesta: 'Las lecturas de carta natal completas generalmente toman entre 60-90 minutos, dependiendo de la profundidad del análisis.',
              },
              {
                pregunta: '¿Los cursos tienen certificación?',
                respuesta: 'Sí, todos nuestros cursos incluyen certificado de finalización reconocido por la Asociación Astrológica Internacional.',
              },
              {
                pregunta: '¿Puedo acceder a los cursos de forma permanente?',
                respuesta: 'Absolutamente. Una vez que compras un curso, tienes acceso de por vida al contenido y actualizaciones futuras.',
              },
              {
                pregunta: '¿Ofrecen consultas presenciales?',
                respuesta: 'Sí, realizamos consultas presenciales en nuestra ubicación en CDMX, previa cita programada.',
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                className="p-6 rounded-xl bg-purple-950/40 border border-purple-500/20"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <h4 className="font-semibold text-purple-200 mb-2">{faq.pregunta}</h4>
                <p className="text-sm text-purple-100/60">{faq.respuesta}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contacto;
